import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Field, FieldWrapper, Form, FormElement } from "@progress/kendo-react-form";
import { Input, TextArea } from "@progress/kendo-react-inputs";
import { Error } from "@progress/kendo-react-labels";
import { Popup } from "@progress/kendo-react-popup";
import useOnClickOutside from "use-onclickoutside";
import { icon } from "../../../config";
import { useWorkflowContext } from "../WorkflowContextProvider";
import { useVariablesContext } from "./context/variableContext";
import { useSelectedVariableValues } from "./hooks/workflowVariables.hooks";
import { VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS, newVariableTypes } from "./workflowVariables.constants";

/**
 * AddVariablePopup component for adding new workflow variables
 * @author Abhishek Dhillon
 * @parent WorkflowVariables
 * @returns {JSX.Element} The AddVariablePopup component
 */
const AddVariablePopup = () => {
  const { user } = useSelector((state) => state.auth || {});
  const teamChipName = (user?.operationalTeamName || "Project").replace(/s$/i, "") || "Project";
  const [anchorAlign, setAnchorAlign] = React.useState({
    horizontal: "middle",
    vertical: "top",
  });
  const [popupAlign, setPopupAlign] = React.useState({
    horizontal: "middle",
    vertical: "top",
  });

  const {
    variableTypeRef,
    setVariableTypeRef,
    setIsNewWorkflowVariable,
    setSelectedVariable,
    setVariableType,
    workflowDefaults,
    customWorkflowVariables,
    workflow,
  } = useWorkflowContext();
  const isFormAssociatedWorkflow = Boolean(workflow?.IsFormWorkflow);
  const isFormChipContext = Boolean(variableTypeRef?.isFormVariable || isFormAssociatedWorkflow);

  const { chipType, setChipType, setDefaultOptions, setHasVarChanges } = useVariablesContext();
  const { setSelectedVariableValues } = useSelectedVariableValues();
  const popupRef = useRef(null);
  /**
   * Handler for closing the popup
   * @author Abhishek Dhillon
   */
  const closePopupHandler = () => {
    setVariableTypeRef({ visible: false, ref: null, type: "" });
  };

  /**
   * Handler for saving the variable
   * @author Abhishek Dhillon
   */
  const saveVariableHandler = async (dataItem) => {
    const chipTypeValue = isFormChipContext ? "smart chip" : "reusable chip";
    await setSelectedVariableValues({ ...dataItem.type, chipType: chipTypeValue, description: dataItem.description }, dataItem.name);
    setSelectedVariable(dataItem.type);
    setVariableType(WORKFLOW_VARIABLE_LABELS.CUSTOM_CHIPS);
    setChipType(chipTypeValue);
    setVariableTypeRef({ visible: false, ref: null, type: "" });
    setHasVarChanges(true);
  };

  /**
   * Get list of variables based on the variable type
   * @author Abhishek Dhillon
   * @returns {Array} Array of variable types
   */
  const getListOfVariables = () => {
    if (variableTypeRef.type === WORKFLOW_VARIABLE_LABELS.CUSTOM_CHIPS) {
      return newVariableTypes;
    }
    return workflowDefaults?.adminCustomVariables;
  };

  /**
   * Render form element for the popup
   * @author Abhishek Dhillon
   * @param {object} formRenderProps - Form render properties
   * @returns {JSX.Element} Form element JSX
   */
  const renderFormElement = (formRenderProps) => {
    return (
      <FormElement>
        <div className='d-flex justify-content-between align-items-center'>
          {variableTypeRef.type === WORKFLOW_VARIABLE_LABELS.CUSTOM_CHIPS ? WORKFLOW_VARIABLE_LABELS.ADD_CHIP : `Add ${teamChipName} Chip`}
          <div className='cursor-pointer text-right'>
            <span onClick={closePopupHandler} className='text-right cursor-pointer'>
              {icon.CLOSE}
            </span>
          </div>
        </div>
        <hr />
        <Field
          maxLength={30}
          name='name'
          component={ValidatedInput}
          label='Name'
          validator={(val) =>
            val.length > 0
              ? customWorkflowVariables?.some((variable) => variable?.name?.toLowerCase() === val.toLowerCase())
                ? WORKFLOW_VARIABLE_LABELS.VARIABLE_NAME_DUPLICATE_ERROR
                : null
              : WORKFLOW_VARIABLE_LABELS.NAME_REQUIRED
          }
        />
        <Field
          name='type'
          component={ValidatedDropDownList}
          label={variableTypeRef.type === WORKFLOW_VARIABLE_LABELS.CUSTOM_CHIPS ? "Field Type" : "Preset"}
          data={getListOfVariables()}
          defaultValue={getListOfVariables()?.[0]}
          textField='variableName'
          validator={(val) => (val ? null : WORKFLOW_VARIABLE_LABELS.FIELD_TYPE_REQUIRED)}
        />
        {variableTypeRef.type === WORKFLOW_VARIABLE_LABELS.CUSTOM_CHIPS ? (
          <Field
            name='description'
            component={ValidatedTextArea}
            label='Description'
            maxLength={100}
            validator={(val) => (val && val?.length > 100 ? "Description cannot exceed 100 characters" : null)}
          />
        ) : (
          <ReadOnlyPresetDescription label='Description' selectedVariable={formRenderProps.valueGetter("type")} />
        )}
        <hr />
        <div className='d-flex justify-content-end text-right'>
          <Button disabled={!formRenderProps.valid} id='saveVariableButton' size='small' themeColor={"primary"} type='submit'>
            {WORKFLOW_VARIABLE_LABELS.SAVE}
          </Button>
        </div>
      </FormElement>
    );
  };
  return (
    <Popup
      anchor={variableTypeRef?.ref}
      anchorAlign={variableTypeRef?.isFormVariable ? { horizontal: "center", vertical: "top" } : anchorAlign}
      popupAlign={variableTypeRef?.isFormVariable ? { horizontal: "center", vertical: "top" } : popupAlign}
      show={variableTypeRef?.visible}
      className='variable-popup '
      collision={{ horizontal: "fit", vertical: "fit" }}
      popupClass={"mr-3 mb-4 p-2"}
      onOpen={() => {
        setDefaultOptions([]);
        setIsNewWorkflowVariable(true);
      }}
      onClose={closePopupHandler}>
      <div className='p-1' ref={popupRef}>
        <Form
          initialValues={{
            name: "",
            type: getListOfVariables()?.[0],
            description: "",
          }}
          onSubmit={saveVariableHandler}
          render={(formRenderProps) => renderFormElement(formRenderProps)}
        />
      </div>
    </Popup>
  );
};

/**
 * Validated input component
 * @author Abhishek Dhillon
 * @param {object} fieldRenderProps - Field render properties
 * @returns {JSX.Element} Validated input JSX
 */
const ValidatedInput = (fieldRenderProps) => {
  const { validationMessage, visited, label, id, valid, disabled, ...others } = fieldRenderProps;
  return (
    <FieldWrapper>
      <label>{label}</label>
      <Input {...others} autoFocus={true} />
      {visited && validationMessage && <Error>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

/**
 * Validated dropdown list component
 * @author Abhishek Dhillon
 * @param {object} fieldRenderProps - Field render properties
 * @returns {JSX.Element} Validated dropdown list JSX
 */
const ValidatedDropDownList = (fieldRenderProps) => {
  const { validationMessage, visited, label, id, valid, disabled, ...others } = fieldRenderProps;
  return (
    <FieldWrapper>
      <label>{label}</label>
      <DropDownList {...others} itemRender={chipItemRender} />
      {visited && validationMessage && <Error>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

/**
 * Validated textarea component for description
 * @param {object} fieldRenderProps - Field render properties
 * @returns {JSX.Element} Validated textarea JSX
 */
const ValidatedTextArea = (fieldRenderProps) => {
  const { validationMessage, visited, label, id, valid, disabled, maxLength, value, ...others } = fieldRenderProps;
  const currentLength = value?.length || 0;
  return (
    <FieldWrapper>
      <div className='d-flex justify-content-between align-items-center'>
        <label>{label}</label>
        <span className='text-muted small'>
          {currentLength}/{maxLength}
        </span>
      </div>
      <TextArea {...others} value={value} maxLength={maxLength} rows={3} style={{ overflowY: "scroll", resize: "none" }} />
      {visited && validationMessage && <Error>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

/**
 * Read-only description component for admin chips
 * @param {object} props - Component properties
 * @returns {JSX.Element} Read-only description JSX
 */
export const ReadOnlyPresetDescription = ({ label, selectedVariable }) => {
  const description = selectedVariable?.description || "No description available";
  return (
    <FieldWrapper>
      <label>{label}</label>
      <TextArea
        value={description}
        readOnly={true}
        rows={3}
        style={{
          overflowY: "scroll",
          resize: "none",
          backgroundColor: "#f5f5f5",
          cursor: "default",
        }}
      />
    </FieldWrapper>
  );
};

const presetItemRender = (li, itemProps) => {
  const itemChildren = (
    <div className='d-flex align-items-center justify-content-between cursor-pointer'>
      <div className='d-flex align-items-start gap-2'>
        <span>{icon.LOCK_ICON}</span>
        <div className='d-flex flex-column'>
          <span>{itemProps?.dataItem?.variableName}</span>
          <span>{WORKFLOW_VARIABLE_LABELS.ADMIN_FIELD}</span>
        </div>
      </div>
    </div>
  );
  return React.cloneElement(li, li.props, itemChildren);
};

const chipItemRender = (li, itemProps) => {
  const itemChildren = (
    <div className='d-flex align-items-center justify-content-between cursor-pointer'>
      <div className='d-flex align-items-start gap-2'>
        <span>{VARIABLE_ICONS[itemProps?.dataItem?.variableType]}</span>
        <div className='d-flex flex-column'>
          <span>{itemProps?.dataItem?.variableName}</span>
        </div>
      </div>
    </div>
  );
  return React.cloneElement(li, li.props, itemChildren);
};

export default AddVariablePopup;
